import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Check, Copy, KeyRound, Trash2 } from 'lucide-react'
import { useAgentTokens, useMintAgentToken, useRevokeAgentToken } from '../hooks/useAgentTokens'
import type { MintedToken } from '../api/agentTokensApi'

/**
 * Connect an AI assistant to your Arthaus account.
 *
 * Two things this screen has to get right, both about the artist rather than the code.
 *
 * The token is shown ONCE — the server keeps only its id — so the reveal has to be
 * unmissable and has to say so plainly, or we generate a support conversation for every
 * artist who closes the panel too early.
 *
 * And the copy says what the token CAN'T do, not just what it can. An artist handing a
 * credential to a third-party AI client is right to want to know the blast radius, and
 * "it cannot publish or delete anything" is the honest and reassuring answer.
 */
export function AgentTokensCard() {
  const [label, setLabel] = useState('')
  const [minted, setMinted] = useState<MintedToken | null>(null)
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useAgentTokens()
  const mint = useMintAgentToken()
  const revoke = useRevokeAgentToken()

  const copy = async () => {
    if (!minted) return
    await navigator.clipboard.writeText(minted.token)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const live = (data?.tokens ?? []).filter((t) => !t.revoked_at)
  const revoked = (data?.tokens ?? []).filter((t) => t.revoked_at)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          Connect an AI assistant
        </CardTitle>
        <CardDescription>
          Create a key so an AI assistant (like Claude) can read your profile, see your
          subscriber count, tidy your links and draft biography chapters for you.
          <strong className="text-slate-700">
            {' '}It cannot publish anything, delete anything, or change your account.
          </strong>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {minted ? (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-900">
              Copy this now — you will not be able to see it again.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-white px-3 py-2 text-xs">
                {minted.token}
              </code>
              <Button size="sm" variant="outline" onClick={copy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-2 text-xs text-emerald-800">{minted.notice}</p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setMinted(null)}>
              Done
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="What is this for? e.g. Claude on my laptop"
              className="max-w-xs"
            />
            <Button
              disabled={mint.isPending}
              onClick={() =>
                mint.mutate(label || undefined, {
                  onSuccess: (t) => { setMinted(t); setLabel('') },
                })
              }
            >
              Create key
            </Button>
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="space-y-2">
            {live.map((t) => (
              <div
                key={t.jti}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3"
              >
                <div>
                  <div className="text-sm font-medium">{t.label || 'Unnamed key'}</div>
                  <div className="text-xs text-slate-500">
                    {t.last_used_at
                      ? `last used ${new Date(t.last_used_at).toLocaleDateString()}`
                      : 'never used'}
                    {' · '}expires {new Date(t.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(t.jti)}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Revoke
                </Button>
              </div>
            ))}
            {!live.length && !minted && (
              <p className="text-sm text-slate-500">No keys yet.</p>
            )}
            {revoked.length > 0 && (
              <p className="pt-1 text-xs text-slate-400">
                {revoked.length} revoked key{revoked.length === 1 ? '' : 's'} hidden.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
